import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import {
  addAptVendor,
  aptVendorAction,
  fetchAptVendors,
  fetchMyAptRole
} from 'api/apartmentProcurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

// The HQ-controlled vendor master, surfaced as its own Settings tab (2026-07-19).
// Same data + actions as the VendorManager dialog on the register, rendered
// inline as a page. Anyone in the module can propose a vendor; Procurement HQ
// approves it before it can be picked on a request line.
const useStyles = makeStyles((theme) => ({
  addRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
    margin: theme.spacing(1.5, 0, 2)
  },
  chip: { height: 20, fontSize: 11 },
  cell: { padding: '4px 10px', fontSize: 13 }
}))

const STATUS_CHIP = {
  pending: { label: 'Pending HQ', color: '#8a6d00', bg: '#ffecb3' },
  approved: { label: 'Approved', color: '#1b5e20', bg: '#d7f5da' },
  rejected: { label: 'Rejected', color: '#b71c1c', bg: '#ffdde0' }
}

const HqVendors = () => {
  const classes = useStyles()
  const [vendors, setVendors] = React.useState(null)
  const [role, setRole] = React.useState({ is_hq: true, is_central: false })
  const [form, setForm] = React.useState({ name: '', gstin: '', contact: '' })
  const [busy, setBusy] = React.useState(false)

  const isHq = !!(role && (role.is_hq || role.is_central))

  const load = React.useCallback(() => {
    fetchAptVendors().then(setVendors).catch(() => setVendors([]))
  }, [])

  React.useEffect(() => {
    load()
    fetchMyAptRole()
      .then(setRole)
      .catch(() => setRole({ is_hq: true, is_central: false }))
  }, [load])

  const propose = async () => {
    if (!form.name.trim()) {
      eventAlert('Vendor name is required.', constants.ERROR, 'Vendors')
      return
    }
    setBusy(true)
    try {
      const saved = await addAptVendor(form)
      eventAlert(
        saved.status === 'approved'
          ? `${saved.name} added to the approved list.`
          : `${saved.name} proposed — awaiting HQ approval.`,
        constants.SUCCESS,
        'Vendors'
      )
      setForm({ name: '', gstin: '', contact: '' })
      load()
    } catch (error) {
      eventAlert(error?.message || 'Could not add vendor.', constants.ERROR, 'Vendors')
    } finally {
      setBusy(false)
    }
  }

  const act = async (vendor, action) => {
    setBusy(true)
    try {
      await aptVendorAction(vendor.uuid, action)
      load()
    } catch (error) {
      eventAlert(error?.message || 'Could not save.', constants.ERROR, 'Vendors')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <Typography variant="caption" color="textSecondary">
        The vendor master is controlled by Procurement HQ. Propose a new one
        here; HQ approves it before it can be picked on a request line.
      </Typography>
      <Box className={classes.addRow}>
        <TextField
          size="small"
          variant="outlined"
          label="Vendor name"
          value={form.name}
          onChange={(e) => {
            const v = e.target.value
            setForm((f) => ({ ...f, name: v }))
          }}
        />
        <TextField
          size="small"
          variant="outlined"
          label="GSTIN (optional)"
          value={form.gstin}
          onChange={(e) => {
            const v = e.target.value
            setForm((f) => ({ ...f, gstin: v }))
          }}
        />
        <TextField
          size="small"
          variant="outlined"
          label="Contact (optional)"
          value={form.contact}
          onChange={(e) => {
            const v = e.target.value
            setForm((f) => ({ ...f, contact: v }))
          }}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          disableElevation
          disabled={busy}
          onClick={propose}>
          {isHq ? 'Add vendor' : 'Propose vendor'}
        </Button>
      </Box>

      {!vendors ? (
        <LinearProgress />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.cell}>Vendor</TableCell>
              <TableCell className={classes.cell}>GSTIN</TableCell>
              <TableCell className={classes.cell}>Contact</TableCell>
              <TableCell className={classes.cell}>Status</TableCell>
              {isHq && <TableCell className={classes.cell} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {vendors.map((v) => {
              const s = STATUS_CHIP[v.status] || STATUS_CHIP.pending
              return (
                <TableRow key={v.uuid} hover>
                  <TableCell className={classes.cell}>{v.name}</TableCell>
                  <TableCell className={classes.cell}>{v.gstin || '—'}</TableCell>
                  <TableCell className={classes.cell}>{v.contact || '—'}</TableCell>
                  <TableCell className={classes.cell}>
                    <Chip
                      className={classes.chip}
                      label={s.label}
                      style={{ color: s.color, backgroundColor: s.bg }}
                    />
                  </TableCell>
                  {isHq && (
                    <TableCell className={classes.cell}>
                      {v.status === 'pending' && (
                        <>
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => act(v, 'approve')}>
                            Approve
                          </Button>
                          <Button
                            size="small"
                            disabled={busy}
                            style={{ color: '#b71c1c' }}
                            onClick={() => act(v, 'reject')}>
                            Reject
                          </Button>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
            {!vendors.length && (
              <TableRow>
                <TableCell colSpan={isHq ? 5 : 4} className={classes.cell}>
                  <Typography variant="body2" color="textSecondary">
                    No vendors yet — add the first above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default HqVendors
